<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'

import { useLocalStorageAvailability } from '@/composables/localStorage'
import { useVisibility } from '@/composables/visibility'

import BaseButton from '@/components/Base/BaseButton.vue'
import BaseSlidingModal from '@/Old/components/BaseSlidingModal.vue'

import { ArrowTopRightOnSquareIcon } from '@heroicons/vue/20/solid'

import type { GameContest } from '@/types'

const props = defineProps<{
  contestGame: GameContest
  modelValue: boolean
  storeSlug: string
}>()

const emit = defineEmits(['update:modelValue', 'hasSeenTrackingModal'])
const { isVisible } = useVisibility()

const stepRequiredToPlay = ref(1)
const textRequiredSlug = ref<Record<string, string>>({})

const isInstagram = computed(() => {
  const ua =
    navigator.userAgent ||
    navigator.vendor ||
    (window as Window & typeof globalThis & { opera: string }).opera

  return ua.indexOf('Instagram') > -1
})

const redirectUrl = computed(
  () =>
    props.contestGame?.stores?.find((store) => store?.store?.slug === props.storeSlug)
      ?.trackedTargetUrl,
)

const onClick = () => {
  stepRequiredToPlay.value = 2

  if (useLocalStorageAvailability() === true) {
    const localStorageKey = `hasParticipated-${props.contestGame.id}-${props.contestGame.stores[0].storeId}-${props.contestGame.contestGameBoostType.slug}`
    localStorage.setItem(localStorageKey, 'true')
  }
}

const closeModal = () => {
  emit('update:modelValue', false)
  emit('hasSeenTrackingModal')
}

watch(isVisible, (newVal) => {
  if (newVal) setTimeout(() => closeModal(), 5000)
})

onMounted(() => {
  textRequiredSlug.value =
    props.contestGame.texts[
      props.contestGame.contestGameBoostType?.slug as keyof typeof props.contestGame.texts
    ]
})
</script>

<template>
  <base-sliding-modal :model-value="modelValue">
    <template #content>
      <div class="flex flex-col items-center justify-center text-center">
        <template v-if="stepRequiredToPlay === 1">
          <div class="relative mb-4 h-24 w-24">
            <img src="@/assets/images/hearts.gif" />
          </div>

          <h1 class="text-xl font-extrabold text-gray-700 md:text-2xl">
            {{ contestGame.askActionTitle }}
          </h1>

          <div v-if="contestGame.contestGameBoostType?.slug !== 'custom'" class="mt-4">
            <div class="mx-6">
              <div class="flex items-center py-2">
                <div
                  class="mr-2 flex h-6 w-6 items-center justify-center rounded-full bg-gray-700 text-center text-white shadow-xl md:h-8 md:w-8"
                >
                  <p class="font-bold">1</p>
                </div>
                <div class="flex-1 text-left text-xs text-gray-700">
                  <p>
                    {{ textRequiredSlug.redirection }}
                  </p>
                </div>
              </div>
              <div class="flex items-center py-2">
                <div
                  class="mr-2 flex h-6 w-6 items-center justify-center rounded-full bg-gray-700 text-center text-white shadow-xl md:h-8 md:w-8"
                >
                  <p class="font-bold">2</p>
                </div>
                <div class="text-left text-xs text-gray-700">
                  <p>{{ textRequiredSlug.condition }}️</p>
                </div>
              </div>
              <div class="flex items-center py-2">
                <div
                  class="mr-2 flex h-6 w-6 items-center justify-center rounded-full bg-gray-700 text-center text-white shadow-xl md:h-8 md:w-8"
                >
                  <p class="font-bold">3</p>
                </div>
                <div class="flex-1 text-left text-xs text-gray-700">
                  <p>
                    {{ textRequiredSlug.gift }}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div v-else>
            <p class="my-4 text-gray-700">
              {{ contestGame.askActionText }}
            </p>
          </div>
          <base-button
            class="mt-4"
            :target="isInstagram ? '_self' : '_blank'"
            is-active
            :href="redirectUrl"
            @click="onClick"
          >
            {{ contestGame.askActionButtonText }}
          </base-button>
        </template>

        <template v-else>
          <h1 class="mb-6 text-xl font-extrabold text-gray-700">
            {{ contestGame.askActionTitle }}
          </h1>

          <div class="flex flex-col items-center justify-center text-center">
            <img src="@/assets/images/loading.gif" class="mx-auto -mt-5 h-24 w-24" />
            <p class="text-lg text-gray-700">{{ contestGame.waitingValidationText }}</p>
            <base-button
              is-white
              :target="isInstagram ? '_self' : '_blank'"
              size="sm"
              class="mt-6"
              is-active
              :href="redirectUrl"
              @click="onClick"
            >
              <ArrowTopRightOnSquareIcon class="mr-2 h-5 w-5" />
              {{ contestGame.askActionButtonText }}
            </base-button>
          </div>
        </template>
      </div>
    </template>
  </base-sliding-modal>
</template>
